/**
 * Live-streaming product card image hydration — seven-marketplace proxy catalog.
 * Placeholder only when scraped gallery array is truly empty.
 */

import { useEffect, useMemo, useState } from "react";
import {
  buildProxyImageUrl,
  collectProductGalleryUrls,
  CHEEP_HOSTED_LUXURY_PLACEHOLDER,
} from "../lib/productImageAssets";
import {
  buildVariationGallery,
  getDefaultVariationSelection,
  getLockedProductImage,
  isVariationLocked,
} from "../lib/variationImageLock";

export function ProductCardHydrated({
  product,
  alt,
  className = "max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300",
  testId,
}) {
  const gallery = useMemo(() => {
    const lockedGallery = buildVariationGallery(product);
    if (lockedGallery.length > 0) return lockedGallery;
    return collectProductGalleryUrls(product);
  }, [product]);
  const initialImage =
    getLockedProductImage(product, getDefaultVariationSelection(product?.variation_matrix)) ||
    gallery[0] ||
    CHEEP_HOSTED_LUXURY_PLACEHOLDER;

  const [displayImage, setDisplayImage] = useState(initialImage);
  const [errorStage, setErrorStage] = useState(0);

  useEffect(() => {
    setDisplayImage(initialImage);
    setErrorStage(0);
  }, [initialImage, product?.product_id]);

  const handleImageError = () => {
    if (isVariationLocked(product)) {
      if (displayImage !== CHEEP_HOSTED_LUXURY_PLACEHOLDER) {
        setDisplayImage(CHEEP_HOSTED_LUXURY_PLACEHOLDER);
      }
      return;
    }
    if (errorStage === 0 && gallery.length > 1) {
      setErrorStage(1);
      setDisplayImage(gallery[1]);
      return;
    }
    if (errorStage <= 1 && gallery[0]) {
      setErrorStage(2);
      setDisplayImage(buildProxyImageUrl(gallery[0]));
      return;
    }
    if (displayImage !== CHEEP_HOSTED_LUXURY_PLACEHOLDER) {
      setErrorStage(3);
      setDisplayImage(CHEEP_HOSTED_LUXURY_PLACEHOLDER);
    }
  };

  return (
    <img
      src={displayImage}
      alt={alt || product?.title || product?.name || "Product"}
      className={className}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      data-testid={testId}
      onError={handleImageError}
    />
  );
}

export default ProductCardHydrated;
